/**
 * 通知服务 - 测评结果通知
 * 
 * 配置说明：
 * 1. 邮件通知：基于 EmailJS REST API（免费，纯前端可用）
 *    - 前往 https://www.emailjs.com/ 注册账号
 *    - 添加 Email Service（如 Gmail）
 *    - 创建 Email Template，收件人设为 zhuhuiling@ysqingxin.com
 *    - 将 Service ID / Template ID / Public Key 填入下方配置
 * 
 * 2. 企业微信通知：基于群机器人 Webhook
 *    - 在企业微信群中添加群机器人
 *    - 获取 Webhook Key
 *    - 将 Key 填入下方配置
 */

// ─── 通知配置 ────────────────────────────
const CONFIG = {
  // EmailJS 配置（需在 emailjs.com 注册并填写）
  emailjs: {
    serviceId: '',      // EmailJS Service ID
    templateId: '',     // EmailJS Template ID
    publicKey: '',      // EmailJS Public Key
  },
  // 企业微信群机器人 Webhook Key（如需群推送通知，请填写实际 Key）
  wecomWebhookKey: '',
}

// 客服邮箱
const CS_EMAIL = 'zhuhuiling@ysqingxin.com'

// ─── 测评结果摘要生成 ────────────────────
interface NotifyData {
  companyName: string
  contactName: string
  phone: string
  email: string
  scenes: { name: string; rank: number }[]
  industry: string
  teamSize: string
  cohesion: number
  resilience: number
  growth: number
  overallIndex: number
  weakestDimension: string
  riskLevel: string
  timestamp: string
}

function buildSummary(data: NotifyData): string {
  const sceneStr = data.scenes
    .sort((a, b) => a.rank - b.rank)
    .map(s => `${['①','②','③'][s.rank - 1]}${s.name}`)
    .join(' + ')

  return [
    `🚨 【新客户提交测评 - 请及时跟进】`,
    ``,
    `━━━━━━━━━━━━━━━━━━━━`,
    `📋 客户信息`,
    `━━━━━━━━━━━━━━━━━━━━`,
    `公司：${data.companyName}`,
    `联系人：${data.contactName}`,
    `手机：${data.phone || '未填写'}`,
    `邮箱：${data.email || '未填写'}`,
    ``,
    `━━━━━━━━━━━━━━━━━━━━`,
    `📊 测评概况`,
    `━━━━━━━━━━━━━━━━━━━━`,
    `挑战场景：${sceneStr}`,
    `行业：${data.industry} | 规模：${data.teamSize}`,
    ``,
    `凝聚力资本：${data.cohesion}`,
    `抗逆力资本：${data.resilience}`,
    `成长力资本：${data.growth}`,
    `综合指数：${data.overallIndex}`,
    `最大短板：${data.weakestDimension}`,
    `风险等级：${data.riskLevel}`,
    ``,
    `⚠️ 请尽快联系客户，安排顾问解读`,
    `提交时间：${data.timestamp}`,
  ].join('\n')
}

// ─── 邮件通知（EmailJS REST API）──────────
export async function sendEmail(data: NotifyData): Promise<boolean> {
  // 未配置 EmailJS → 使用 mailto 兜底
  if (!CONFIG.emailjs.serviceId || !CONFIG.emailjs.templateId || !CONFIG.emailjs.publicKey) {
    const subject = encodeURIComponent(`【测评提醒】${data.companyName} 完成团队心力资本测评`)
    const body = encodeURIComponent(buildSummary(data))
    window.open(`mailto:${CS_EMAIL}?subject=${subject}&body=${body}`, '_blank')
    return true
  }

  try {
    const resp = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        service_id: CONFIG.emailjs.serviceId,
        template_id: CONFIG.emailjs.templateId,
        user_id: CONFIG.emailjs.publicKey,
        template_params: {
          to_email: CS_EMAIL,
          subject: `【测评提醒】${data.companyName} 完成团队心力资本测评`,
          message: buildSummary(data),
          company: data.companyName,
          contact: data.contactName,
          phone: data.phone || '未填写',
          email: data.email || '未填写',
          scenes: data.scenes.map(s => s.name).join(', '),
          scores: `凝聚力${data.cohesion} / 抗逆力${data.resilience} / 成长力${data.growth}`,
          overall: String(data.overallIndex),
          risk: data.riskLevel,
        },
      }),
    })
    return resp.ok
  } catch {
    return false
  }
}

// ─── 企业微信 Webhook 通知 ────────────────
export async function sendWeComNotification(data: NotifyData): Promise<boolean> {
  if (!CONFIG.wecomWebhookKey) {
    console.warn('[企微通知] Webhook Key 未配置，请在 notify.ts 中填写 CONFIG.wecomWebhookKey')
    return false
  }

  const webhookUrl = `https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=${CONFIG.wecomWebhookKey}`
  const summary = buildSummary(data)

  try {
    const resp = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        msgtype: 'text',
        text: {
          content: summary,
          mentioned_list: ['@all'],
        },
      }),
    })
    if (resp.ok) {
      console.log('[企微通知] 发送成功')
    } else {
      console.error('[企微通知] 发送失败:', resp.status, resp.statusText)
    }
    return resp.ok
  } catch (err) {
    console.error('[企微通知] 网络错误:', err)
    return false
  }
}

// ─── 预约通知（用户点击"预约顾问"时调用）────────────────
interface BookingData {
  companyName: string
  contactName: string
  phone: string
  email: string
  timestamp: string
}

// 企微客服 KFID 链接（用于客服接入）
const WEWORK_KF_LINK = 'https://work.weixin.qq.com/kfid/kfcc1729b8642e445e3'

export async function sendBookingNotification(data: BookingData): Promise<boolean> {
  const content = [
    `【🔔 客户预约顾问通知】`,
    ``,
    `━━━━━━━━━━━━━━━━━━━━`,
    `📋 预约客户信息`,
    `━━━━━━━━━━━━━━━━━━━━`,
    `公司：${data.companyName}`,
    `联系人：${data.contactName}`,
    `手机号：${data.phone || '未填写'}`,
    `邮箱：${data.email || '未填写'}`,
    ``,
    `⚠️ 该客户已完成《团队心力资本初评》，现主动预约顾问连线，请尽快联系！`,
    ``,
    `💬 企微客服入口：${WEWORK_KF_LINK}`,
    `提交时间：${data.timestamp}`,
  ].join('\n')

  // 1. 企微 Webhook 群通知
  if (CONFIG.wecomWebhookKey) {
    try {
      const webhookUrl = `https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=${CONFIG.wecomWebhookKey}`
      const resp = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          msgtype: 'text',
          text: { content, mentioned_list: ['@all'] },
        }),
      })
      if (resp.ok) return true
    } catch {
      // 兜底到 mailto
    }
  }

  // 2. 兜底：mailto 邮件通知（包含企微客服链接）
  const subject = encodeURIComponent(`【预约提醒】${data.companyName} 预约顾问连线`)
  const body = encodeURIComponent(content)
  window.open(`mailto:${CS_EMAIL}?subject=${subject}&body=${body}`, '_blank')
  return true
}

// ─── 统一通知入口 ────────────────────────
export async function sendNotifications(data: NotifyData): Promise<{
  email: boolean
  wecom: boolean
}> {
  const [emailOk, wecomOk] = await Promise.all([
    sendEmail(data),
    sendWeComNotification(data),
  ])

  return { email: emailOk, wecom: wecomOk }
}

export { CS_EMAIL }
